/*
 * Чистая математика доски в звонке: модель штриха, прореживание точек при
 * записи и попадание ластика — без DOM и без React, чтобы проверить тестами
 * (см. заголовок callMath.ts — тот же приём). Модель штриха взята из
 * редактора пометок на фото (PhotoEditor.tsx): точки в долях от размера
 * листа, а не в пикселях экрана.
 */

export type Pt = { x: number; y: number };

/**
 * Штрих листа. Автор нужен только для отмены и ластика — в интерфейсе не
 * показывается. `id` — номер штриха на сервере; 0, пока ответ на запись ещё
 * не пришёл (правка 10.09.2026, пункт 1 работы над живым слоем). `nonce` —
 * случайная метка живого слоя (раздел 4.2): её, в отличие от `id`, отправитель
 * знает сразу, до ответа сервера, и по ней остальные участники узнают штрих
 * в сообщениях отмены и стирания, пришедших по каналу данных мгновенно.
 * У штрихов, прочитанных обычным чтением листа с сервера, nonce нет.
 */
/**
 * Штрих листа. `shape` есть у распознанных фигур (shapes.ts): по точкам не
 * понять, чем росчерк был, а значит нечем и двигать его как фигуру. Обычный
 * росчерк её не имеет.
 */
export type Stroke = {
  id: number;
  author: string;
  color: string;
  width: number;
  pts: Pt[];
  nonce?: string;
  shape?: import('./shapes').Shape | null;
};

export type BoardColor = { cls: 'red' | 'yellow' | 'blue' | 'green' | 'black'; value: string };
/** Палитра панели рисования (design-system 3.35) — инструмент, а не тема, четыре цвета. */
export const BOARD_COLORS: BoardColor[] = [
  { cls: 'red', value: '#E5322D' },
  { cls: 'yellow', value: '#F5C400' },
  { cls: 'blue', value: '#2F6FBF' },
  { cls: 'green', value: '#17A05E' },
  { cls: 'black', value: '#17181C' },
];

export type BoardWidthOpt = { cls: 'sm' | 'md' | 'lg'; value: number };
/** Толщины — доли ширины листа, та же палитра, что в PhotoEditor.tsx и sheets.service.ts. */
export const BOARD_WIDTHS: BoardWidthOpt[] = [
  { cls: 'sm', value: 0.005 },
  { cls: 'md', value: 0.011 },
  { cls: 'lg', value: 0.02 },
];

/** Чистый лист — альбомный лист А4 (лист занимает всю сцену звонка, ширина больше высоты). */
export function blankSheetSize(): { width: number; height: number } {
  return { width: 1600, height: 1131 };
}

/** Больше страниц в загруженном документе браузер не разбирает (раздел 5 спеки). */
export const MAX_SHEET_PDF_PAGES = 200;

/**
 * Что делать с выбранным файлом (раздел 5 спеки): PDF просит выбрать страницу,
 * картинка становится подложкой сразу (как и раньше), всё остальное отклоняется
 * понятным сообщением ещё до попытки разбора.
 */
export function boardFileKind(mime: string): 'pdf' | 'image' | null {
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/png' || mime === 'image/jpeg') return 'image';
  return null;
}

/** Ближе этого расстояния (в долях листа) точка новой формы не несёт — раздувает штрих и запрос. */
export const DECIMATE_MIN_DIST = 0.004;
/** Радиус попадания ластика (в долях листа) — физический размер касания, а не толщина линии. */
export const ERASER_TOLERANCE = 0.025;

/**
 * Прореживание точек штриха при записи (раздел 4.3 спеки): точки ближе
 * minDist друг к другу не меняют форму линии на глаз, но раздувают штрих и
 * запрос на сервер. Первая и последняя точка остаются всегда — иначе
 * одиночный тап или самый конец жеста потерялся бы.
 */
export function decimatePoints(pts: Pt[], minDist: number = DECIMATE_MIN_DIST): Pt[] {
  if (pts.length <= 2) return pts;
  const out: Pt[] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const last = out[out.length - 1];
    const p = pts[i];
    const dx = p.x - last.x;
    const dy = p.y - last.y;
    if (dx * dx + dy * dy >= minDist * minDist) out.push(p);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** Расстояние от точки до отрезка [a, b] — служебная геометрия попадания ластика и фигур. */
export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Точка касается штриха — ближе tolerance к какому-нибудь его отрезку (или к самой точке для тапа). */
export function hitsStroke(p: Pt, stroke: Stroke, tolerance: number): boolean {
  if (!stroke.pts.length) return false;
  if (stroke.pts.length === 1) {
    return Math.hypot(p.x - stroke.pts[0].x, p.y - stroke.pts[0].y) <= tolerance;
  }
  for (let i = 1; i < stroke.pts.length; i++) {
    if (distToSegment(p, stroke.pts[i - 1], stroke.pts[i]) <= tolerance) return true;
  }
  return false;
}

/**
 * Последний штрих заданного автора в списке (порядок — время прихода на
 * сервер, раздел 4.2 спеки). И отмена, и ластик снимают именно его: сервер
 * умеет отменять только последний свой штрих (раздел 3.3), не произвольный.
 */
export function myLastStroke(strokes: Stroke[], author: string): Stroke | null {
  for (let i = strokes.length - 1; i >= 0; i--) {
    if (strokes[i].author === author) return strokes[i];
  }
  return null;
}

/**
 * Штрих СВОЕГО автора, которого коснулся ластик, — не обязательно последний
 * (правка волны доски 10.09.2026): перебор всех своих штрихов с конца через
 * то же попадание (hitsStroke, геометрия не меняется), первое совпадение —
 * самый недавний из подходящих. Чужие штрихи не рассматриваются вовсе.
 * Сервер теперь умеет стирать конкретный штрих по id (sheets.service.ts,
 * eraseStroke), а не только последний.
 */
export function eraserHit(p: Pt, strokes: Stroke[], author: string, tolerance: number = ERASER_TOLERANCE): Stroke | null {
  for (let i = strokes.length - 1; i >= 0; i--) {
    const s = strokes[i];
    if (s.author === author && hitsStroke(p, s, tolerance)) return s;
  }
  return null;
}

/*
 * Предохранители нагрузки живого слоя (раздел 4.3 спеки, задача владельца
 * продукта 10.09.2026): прореживание точек уже есть выше (decimatePoints,
 * тот же приём, что в PhotoEditor.tsx). Здесь — два новых предела.
 */

/**
 * Точек в одном штрихе не больше этого — иначе жест без остановки режется на
 * несколько отдельных штрихов. Число выбрано от DECIMATE_MIN_DIST: после
 * прореживания 400 точек — это путь длиной около 1.6 в долях листа, то есть
 * больше диагонали самого листа (~1.4 у альбомного А4-подобного), — жест
 * короче одной полной диагонали никогда не режется, а безостановочная
 * штриховка на весь экран и дальше рубится на куски вместо одного гигантского
 * штриха. Сильно ниже серверного жёсткого предела (MAX_STROKE_POINTS = 5000,
 * sheets.service.ts) — режем задолго до него, не полагаясь на отказ сервера.
 */
export const MAX_STROKE_LEN = 400;

/**
 * Режет точки штриха на куски не длиннее maxLen (раздел 4.3): последняя точка
 * предыдущего куска становится первой точкой следующего, чтобы линия на
 * стыке не разрывалась. Короткий штрих — один кусок, без изменений.
 */
export function chunkStroke(pts: Pt[], maxLen: number = MAX_STROKE_LEN): Pt[][] {
  if (pts.length <= maxLen) return pts.length ? [pts] : [];
  const out: Pt[][] = [];
  let i = 0;
  while (i < pts.length - 1) {
    const end = Math.min(i + maxLen, pts.length);
    out.push(pts.slice(i, end));
    i = end - 1; // стык: последняя точка куска = первая точка следующего
  }
  return out;
}

/**
 * Не чаще этого штрихов в секунду уходит от одного человека (раздел 4.3):
 * штрих отправляется целиком по завершении жеста, но предел точек в штрихе
 * (MAX_STROKE_LEN) сам по себе режет безостановочную штриховку на частые
 * отдельные отправки — без предела частоты бешеный жест мог бы слать очередь
 * из штрихов быстрее, чем канал и сервер успевают их принять. Пять в секунду
 * — с запасом больше, чем успевает нарисовать рука (обычный штрих — от
 * трети секунды до пары секунд), и заметно меньше, чем библиотека и сервер
 * способны выдержать от одного участника.
 */
export const SEND_RATE_LIMIT = 5;
export const SEND_RATE_WINDOW_MS = 1000;

/**
 * Решает, можно ли отправить штрих прямо сейчас, не превышая предел частоты:
 * `history` — метки времени уже отправленного за последнее окно. Можно —
 * возвращает `delayMs: 0` и историю с добавленной меткой `now`; нельзя —
 * `delayMs` до момента, когда самая старая отправка выйдет из окна, и
 * историю без изменений (штрих ещё не ушёл, добавлять в неё нечего). Чистая
 * функция: сам таймер и очередь отправки — на вызывающей стороне.
 */
export function throttleSend(
  history: number[],
  now: number,
  limit: number = SEND_RATE_LIMIT,
  windowMs: number = SEND_RATE_WINDOW_MS,
): { delayMs: number; history: number[] } {
  const recent = history.filter((t) => now - t < windowMs);
  if (recent.length < limit) {
    return { delayMs: 0, history: [...recent, now] };
  }
  return { delayMs: Math.max(0, recent[0] + windowMs - now), history: recent };
}

/*
 * Живой слой (раздел 4.2 спеки): законченный штрих уходит по каналу данных
 * медиасервера всем участникам мгновенно, а хранимый слой (обычный запрос на
 * сервер) остаётся источником истины и подстраховкой от потери пакета.
 * Дальше — чистая часть защиты от двойного применения.
 */

/**
 * Свой же штрих, пришедший обратно по каналу данных, второй раз рисовать не
 * нужно — он уже нарисован оптимистично в момент, когда его провели рукой.
 * Чужой штрих с уже виденным nonce — тоже повтор (переотправка каналом
 * данных не гарантирует ровно одну доставку). `seen` не меняется здесь —
 * чистая функция только решает, применять ли, пополнение множества увиденных
 * nonce — дело вызывающей стороны.
 */
export function shouldApplyIncoming(nonce: string, author: string, me: string, seen: ReadonlySet<string>): boolean {
  if (author === me) return false;
  return !seen.has(nonce);
}

/**
 * Убирает штрих, названный отменой или ластиком, пришедшими по каналу данных
 * (раздел 4.2): ищет сперва по nonce живого слоя (штрих, который эта вкладка
 * уже видела живьём), а если nonce не совпал или его не было (штрих узнан из
 * обычного чтения листа, до того как эта вкладка вошла в разговор) — по
 * номеру сервера. Ничего не найдено — список не меняется, штрих поправит
 * следующее полное чтение (спека прямо допускает такую потерю пакета).
 *
 * `author` — личность отправителя канала данных (важно 7), не поле `author`
 * из тела сообщения: nonce и id штриха видны всем в комнате (штрих сам
 * рассылается живым слоем), значит их одних недостаточно — участник мог бы
 * прислать чужие nonce/id со своим сообщением и стереть чужой штрих.
 * Снимается только штрих, у которого автор совпал с отправителем.
 */
export function removeStroke(strokes: Stroke[], target: { nonce?: string; id?: number; author: string }): Stroke[] {
  const idx = strokes.findIndex(
    (s) => s.author === target.author && ((target.nonce && s.nonce === target.nonce) || (!!target.id && s.id === target.id)),
  );
  if (idx < 0) return strokes;
  return [...strokes.slice(0, idx), ...strokes.slice(idx + 1)];
}

/**
 * Сообщение живого слоя (раздел 4.2): законченный штрих или его снятие
 * (отмена/ластик).
 *
 * Те же сообщения носят пометки поверх демонстрации экрана (решение Ильи
 * 16.09.2026) — с одной разницей: у них нет хранимого слоя. Помеченный
 * `over: true` штрих живёт только в комнате, на сервер не уходит и в итоговый
 * файл не попадает. `clear` стирает такие пометки у всех разом — это и есть
 * кнопка «Удалить всё».
 */
export type BoardMsg =
  | { t: 'stroke'; nonce: string; author: string; color: string; width: number; pts: Pt[]; over?: boolean }
  | { t: 'remove'; author: string; nonce?: string; id?: number }
  /** Фигуру передвинули или растянули (решение Ильи 16.09.2026): те же точки, новое место. */
  | { t: 'move'; author: string; nonce?: string; id?: number; pts: Pt[] }
  | { t: 'clear'; author: string };

/** Кодирует сообщение живого слоя для канала данных медиасервера — он носит байты, не строки. */
export function encodeBoardMsg(msg: BoardMsg): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(msg));
}

const isPt = (v: unknown): v is Pt =>
  !!v && typeof v === 'object' && Number.isFinite((v as Pt).x) && Number.isFinite((v as Pt).y);

/**
 * Разбирает сообщение живого слоя; чужая или битая посылка — `null`, а не
 * исключение: канал данных несёт байты кого угодно из комнаты, доверять их
 * форме нельзя (тот же принцип, что и у assertStroke на сервере).
 */
export function decodeBoardMsg(bytes: Uint8Array): BoardMsg | null {
  let data: unknown;
  try {
    data = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const m = data as Record<string, unknown>;
  if (m.t === 'stroke') {
    if (
      typeof m.nonce !== 'string' ||
      typeof m.author !== 'string' ||
      typeof m.color !== 'string' ||
      typeof m.width !== 'number' ||
      !Array.isArray(m.pts) ||
      !m.pts.every(isPt)
    ) {
      return null;
    }
    return {
      t: 'stroke',
      nonce: m.nonce,
      author: m.author,
      color: m.color,
      width: m.width,
      pts: m.pts as Pt[],
      ...(m.over === true ? { over: true as const } : {}),
    };
  }
  if (m.t === 'clear') {
    if (typeof m.author !== 'string') return null;
    return { t: 'clear', author: m.author };
  }
  if (m.t === 'move') {
    if (typeof m.author !== 'string' || !Array.isArray(m.pts) || !m.pts.every(isPt)) return null;
    const nonce = typeof m.nonce === 'string' ? m.nonce : undefined;
    const id = typeof m.id === 'number' ? m.id : undefined;
    return { t: 'move', author: m.author, nonce, id, pts: m.pts as Pt[] };
  }
  if (m.t === 'remove') {
    if (typeof m.author !== 'string') return null;
    const nonce = typeof m.nonce === 'string' ? m.nonce : undefined;
    const id = typeof m.id === 'number' ? m.id : undefined;
    return { t: 'remove', author: m.author, nonce, id };
  }
  return null;
}

/**
 * Подменяет точки штриха, который передвинули (сообщение `move` живого слоя).
 * Ищет так же, как removeStroke, и по тому же правилу: двигается только штрих
 * своего автора — иначе участник двигал бы чужие фигуры на чужих экранах.
 */
export function moveStrokePts(strokes: Stroke[], target: { nonce?: string; id?: number; author: string }, pts: Pt[]): Stroke[] {
  const idx = strokes.findIndex(
    (s) => s.author === target.author && ((target.nonce && s.nonce === target.nonce) || (!!target.id && s.id === target.id)),
  );
  if (idx < 0) return strokes;
  return strokes.map((s, i) => (i === idx ? { ...s, pts } : s));
}

/*
 * Масштаб листа доски (решение Ильи 11.09.2026: «хочу двигать и приближать
 * пальцами»). Формат листа не меняется — меняется только то, как близко на
 * него смотрят. Лист всегда остаётся в виду: за края сцены его не утащить.
 */

/** Как близко можно подойти к листу и как далеко отойти. */
export const BOARD_ZOOM_MIN = 1;
export const BOARD_ZOOM_MAX = 5;

/** Положение листа: масштаб и сдвиг в пикселях сцены. */
export type BoardView = { scale: number; tx: number; ty: number };

export const BOARD_VIEW_FIT: BoardView = { scale: 1, tx: 0, ty: 0 };

export const clampZoom = (scale: number): number =>
  Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN, scale));

/**
 * Не дать листу уехать за край: при масштабе 1 он стоит ровно по центру, а
 * дальше сдвиг ограничен половиной прироста размера.
 */
export function clampPan(view: BoardView, box: { w: number; h: number }): BoardView {
  const scale = clampZoom(view.scale);
  const maxX = (box.w * (scale - 1)) / 2;
  const maxY = (box.h * (scale - 1)) / 2;
  return {
    scale,
    // `|| 0` заодно превращает -0 в 0: минус-ноль ломает сравнение положений
    tx: Math.min(maxX, Math.max(-maxX, view.tx)) || 0,
    ty: Math.min(maxY, Math.max(-maxY, view.ty)) || 0,
  };
}

/**
 * Приблизить или отдалить, оставив под пальцами (или под курсором) ту же
 * точку листа. `center` — в координатах сцены, от её середины.
 */
export function zoomAt(
  view: BoardView,
  factor: number,
  center: { x: number; y: number },
  box: { w: number; h: number },
): BoardView {
  const scale = clampZoom(view.scale * factor);
  const k = scale / view.scale;
  return clampPan(
    {
      scale,
      tx: center.x - (center.x - view.tx) * k,
      ty: center.y - (center.y - view.ty) * k,
    },
    box,
  );
}

/** Сдвинуть лист пальцем или двумя. */
export const panBy = (view: BoardView, dx: number, dy: number, box: { w: number; h: number }): BoardView =>
  clampPan({ scale: view.scale, tx: view.tx + dx, ty: view.ty + dy }, box);

/** Расстояние между двумя касаниями: по нему считается щипок. */
export const touchSpread = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** Середина между двумя касаниями — точка, вокруг которой щиплют. */
export const touchCenter = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});
